import {
  useRef,
  useState
} from 'react'
import {
  useDispatch,
  useSelector
} from 'react-redux'
import {
  Box,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  IconButton,
  Stack,
  Text
} from '@chakra-ui/react'
import {
  ArrowIcon,
  SettingsIcon
} from 'assets/icons'
import {
  AnimatePresence,
  motion
} from 'framer-motion'
import {
  setOpenBlogForm,
  setSocMedDisplay,
  setSocMedDisplayStatus,
  setUserSectionPage,
  usersSelectors,
  setExecAutoArchive,
  setOnPostEditor,
  setCloseAllDrawers
} from 'store'
import {
  BLOGS,
  EDIT_PROFILE,
  SETTINGS,
  SINGLE_PROFILE
} from 'utils/constants'
import {
  isAuth,
  isIOS,
  isNotMobile
} from 'utils/helpers'
import { COLORS } from 'utils/styles'

/* eslint-disable import/no-cycle */
import { UserSection } from 'components'
import { Auth } from 'components/Auth'
import { CropPicture } from '../CropPicture'
import { DiscardChanges } from '../DiscardChanges'

const MotionDrawerContent = motion(DrawerContent)

export const UserDrawer = ({
  onClose,
  dynamicIsOpen,
  isDiscard,
  setIsDiscard,
  blogFinishAnimation,
  isClickedOutside,
  setIsClickedOutside,
  isBlogForMobile,
  handleCloseButton,
  handleSettingButton
}) => {
  const {
    user,
    isBlogFormOpen,
    isSocialMediaSelectOpen,
    isProfileChanged,
    isProfileIncomplete,
    isLocationOpen,
    isProfileDeleted,
    isPictureUploaded,
    closeResetProfileModal,
    cropFor,
    postsTypeStatus,
    userSectionPage,
    onPostEditor,
    postPostsApiMsg
  } = useSelector(usersSelectors)

  const btnRef = useRef()
  const dispatch = useDispatch()
  const [isCloseable, setIsCloseable] = useState(true)
  const [closeCropModal, setCloseCropModal] = useState(true)
  const [selectedUser, setSelectedUser] = useState('')
  let touchStart
  let touchEnd

  const showSettingsIcon = isBlogForMobile()
    || isBlogFormOpen
    || userSectionPage.at(-1) === EDIT_PROFILE
    || userSectionPage.at(-1) === SETTINGS

  const blogHeader = () => {
    if (postPostsApiMsg !== '') {
      return postPostsApiMsg
    }

    if (onPostEditor === 'post') {
      return 'New Blog'
    }

    if (onPostEditor === 'put') {
      return 'Edit Blog'
    }

    if (postsTypeStatus === 'archived') {
      return 'Archived Blogs'
    }

    if (postsTypeStatus === 'scheduled') {
      return 'Scheduled Blogs'
    }

    return 'My Blogs'
  }

  const closingCondition = () => {
    if (
      closeCropModal
      && closeResetProfileModal
    ) {
      if (
        userSectionPage.at(-1) === EDIT_PROFILE
        && isSocialMediaSelectOpen === false
      ) {
        if (isProfileChanged === true && isLocationOpen === false) {
          if (!isIOS()) {
            setIsCloseable(false)
          }
          setIsDiscard(true)
        } else if (
          isLocationOpen === true
          && (isProfileChanged === true || isProfileChanged === false)
        ) {
          if (!isIOS()) {
            setIsCloseable(false)
          }
          setIsDiscard(false)
        } else {
          onClose()
        }
      } else if (
        userSectionPage.at(-1) === EDIT_PROFILE
        && isSocialMediaSelectOpen === true
      ) {
        if (isProfileChanged === true) {
          setIsCloseable(false)
          dispatch(setSocMedDisplay(false))
          dispatch(setSocMedDisplayStatus('none'))
        } else {
          setIsDiscard(false)
          dispatch(setSocMedDisplay(false))
          dispatch(setSocMedDisplayStatus('none'))
        }
      } else if (userSectionPage.at(-1) === SETTINGS) {
        if (isProfileDeleted === true) {
          setIsCloseable(false)
        } else {
          setIsCloseable(true)
          onClose()
        }
      } else {
        if (
          userSectionPage.at(-1) === BLOGS
          && onPostEditor === 'post'
        ) {
          dispatch(setExecAutoArchive(true))
          dispatch(setOnPostEditor(''))
        }

        setIsDiscard(false)
        dispatch(setSocMedDisplay(false))
        dispatch(setSocMedDisplayStatus('none'))
        onClose()
      }
    }
  }

  const animateVariants = {
    initial: {
      x: -500,
      marginBlock: isBlogForMobile() ? '60px' : '60px',
      marginInline: isBlogForMobile() ? '10px' : '10px',
      width: isBlogForMobile() ? '95%' : '95%',
      borderRadius: isBlogForMobile() ? '10px' : '10px'
    },
    animate: {
      x: 0,
      marginBlock: isBlogForMobile() ? '0px' : '60px',
      marginInline: isBlogForMobile() ? '0px' : '10px',
      width: isBlogForMobile() ? '100%' : '95%',
      borderRadius: isBlogForMobile() ? '0px' : '10px',
      transition: {
        type:
          isBlogForMobile()
          || blogFinishAnimation
            ? 'tween'
            : 'spring',
        duration:
          isBlogForMobile()
          || blogFinishAnimation
            ? 1
            : 0.8
      }
    },
    exit: {
      x: -500,
      transition: {
        type: 'tween',
        duration:
          isBlogForMobile()
          || blogFinishAnimation
            ? 1
            : 0.8
      }
    }
  }

  const handleDrawerContent = (pageName) => (
    <MotionDrawerContent
      key={pageName}
      variants={animateVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      onAnimationStart={() => setIsCloseable(false)}
      onAnimationComplete={() => setIsCloseable(true)}
      overflow="hidden"
      maxWidth="450px"
      boxShadow="0 4px 10px rgba(0, 0, 0, 0.2) !important"
    >
      <Stack
        alignItems="center"
        justifyContent="space-between"
        direction="row"
        p="0px"
        background={
          isNotMobile()
            ? COLORS.goldGradientLeftToRight
            : COLORS.goldGradientRightToLeft
        }
        zIndex={5}
        height="48px"
        overflow="hidden"
        onTouchStart={(e) => {
          touchStart = e?.targetTouches[0].clientX
        }}
        onTouchMove={(e) => {
          touchEnd = e?.targetTouches[0].clientX
        }}
        onTouchEnd={() => {
          if (touchStart - touchEnd > 45) {
            closingCondition()
          }
        }}
      >
        <IconButton
          name="profile-pane-back-btn"
          _hover={{ background: COLORS.gold_hover }}
          background="unset"
          marginLeft="5px"
          icon={<ArrowIcon />}
          onClick={() => {
            if (isBlogFormOpen) {
              dispatch(setOpenBlogForm(false))
            } else if (
              pageName === SINGLE_PROFILE
              && onPostEditor === 'post'
            ) {
              dispatch(setExecAutoArchive(true))
              dispatch(setOnPostEditor(''))
            } else {
              handleCloseButton()
            }
          }}
          outline="none"
          boxShadow="none"
          css={{
            ':focus': {
              outline: 'none',
              boxShadow: 'none'
            }
          }}
        />

        {
          userSectionPage.at(-1) === EDIT_PROFILE
          && isSocialMediaSelectOpen
          && (
            <>
              <Text
                whiteSpace="nowrap"
                fontSize="20px"
                sx={{
                  marginLeft: '105px !important',
                  '@media (max-width: 480px)': {
                    marginLeft: '65px !important'
                  }
                }}
                fontWeight={600}
              >
                Choose Platform
              </Text>
              <Box width="95%" />
            </>
          )
        }

        {
          pageName === BLOGS && (
            <Text
              whiteSpace="nowrap"
              fontSize="18px"
              fontWeight={500}
              backgroundColor={postPostsApiMsg === '' ? '' : '#FBFCF8'}
              padding={postPostsApiMsg === '' ? '' : '0 10px'}
              borderRadius={postPostsApiMsg === '' ? '' : '5px'}
            >
              { blogHeader() }
            </Text>
          )
        }

        {
          user
          && !isSocialMediaSelectOpen
          && (
            <IconButton
              _hover={{ background: COLORS.gold_hover }}
              background="unset"
              icon={<SettingsIcon />}
              onClick={handleSettingButton}
              outline="none"
              boxShadow="none"
              opacity={showSettingsIcon ? 0 : 1}
              pointerEvents={showSettingsIcon ? 'none' : 'auto'}
              css={{
                marginRight: '5px',
                ':focus': {
                  outline: 'none',
                  boxShadow: 'none'
                }
              }}
            />
          )
        }
      </Stack>

      <DrawerBody
        id={`${pageName}-drawer-body`}
        sx={{
          overflowX: 'hidden',
          '::MsOverflowStyle': 'none',
          scrollbarWidth: 'none',
          '::-webkit-scrollbar': {
            display: 'none'
          },
          overflowY: isBlogFormOpen ? 'hidden !important' : ''
        }}
        margin="0px"
        padding="0px"
        position="relative"
      >
        {
          isAuth() && user
            ? (
              <UserSection
                pageName={pageName}
                selectedUser={selectedUser}
                setSelectedUser={setSelectedUser}
              />
            )
            : <Auth pageName={pageName} />
        }
      </DrawerBody>
    </MotionDrawerContent>
  )

  return (
    <Drawer
      id="user-drawer"
      isOpen={dynamicIsOpen}
      placement="left"
      onClose={() => {
        if (
          userSectionPage.at(-1) === EDIT_PROFILE
          && isProfileChanged === true
          && isSocialMediaSelectOpen === false
        ) {
          setIsDiscard(true)
        } else {
          dispatch(setUserSectionPage('clear'))
          onClose()
        }
      }}
      finalFocusRef={btnRef}
      size="sm"
      blockScrollOnMount={false}
      closeOnOverlayClick={isCloseable}
      closeOnEsc={false}
      onEsc={() => handleCloseButton()}
    >
      <DrawerOverlay
        zIndex="1399"
        background="transparent"
        onClick={() => {
          if (
            userSectionPage.at(-1) === EDIT_PROFILE
            && isProfileChanged === true
            && isSocialMediaSelectOpen === false
          ) {
            setIsDiscard(true)
          } else {
            dispatch(setCloseAllDrawers(true))
            setTimeout(() => dispatch(setCloseAllDrawers(false)), 850)
          }
        }}
      />

      <AnimatePresence>
        { userSectionPage.map((pageName) => handleDrawerContent(pageName)) }
      </AnimatePresence>

      <DiscardChanges
        isDiscard={isDiscard}
        setIsDiscard={setIsDiscard}
        setIsCloseable={setIsCloseable}
        isProfileIncomplete={isProfileIncomplete}
        isClickedOutside={isClickedOutside}
        setIsClickedOutside={setIsClickedOutside}
        closeTheDrawer={onClose}
      />
      <CropPicture
        isPictureUploaded={isPictureUploaded}
        setCloseCropModal={setCloseCropModal}
        cropFor={cropFor}
      />
    </Drawer>
  )
}
